import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import { rgba } from 'polished';
import { Points, marginLevel } from '@styles';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Rhytm } from 'planoplan-ui-kit/core/helpers/rhytm';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';

export const BlogCards = ({ cards }) => (
  <Container>
    <List>
      {cards.map(({ id, title, url, category, categoryName, description, cover, gradientColor, defaultColor }) => (
        <Item
          key={id}
          cover={cover}
          gradientColor={gradientColor}
          defaultColor={defaultColor}
          isMobile={deviceDetected.isMobile}>
          <Link href={`/blog/${category}/${url}/`}>
            <a>
              <Category>{categoryName}</Category>
              <Title>{title}</Title>
              <Description>{description}</Description>
            </a>
          </Link>
        </Item>
      ))}
    </List>
  </Container>
);

export const Container = styled.div`
  width: 100%;

  @media ${Points.laptop_wide} {
    display: flex;
  }
`;

const List = styled.ul`
  ${Mixins.resetList()};
  min-width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, 280px);
  gap: 0 ${Rhytm.column(1.8, 32)}; // чтобы влезало на 670
  justify-content: center;

  @media ${Points.laptop} {
    grid-template-columns: repeat(auto-fit, 400px);
    gap: 0 ${Rhytm.column(2, 32)};
  }

  @media ${Points.laptop_wide} {
    display: block;
    margin-left: -${Rhytm.column(0.42, 32)};
    margin-right: -${Rhytm.column(0.42, 32)}; // чтобы влезало на 1440
  }
`;

const Category = styled.div`
  font-weight: 300;
  font-size: 14px;
  line-height: 17px;
  margin-bottom: 10px;
`;

const Title = styled.p`
  font-size: 20px;
  line-height: 100%;
  font-weight: 200;

  @media ${Points.laptop} {
    font-size: 32px;
  }

  @media ${Points.laptop_wide} {
    font-size: 52px;
  }
`;

const Description = styled.p`
  display: none;
  font-weight: 400;
  font-size: 26px;
  line-height: 100%;
  margin-top: 60px;

  @media ${Points.laptop} {
    margin-bottom: ${Rhytm.base(5)};
  }
`;

const Item = styled.li`
  ${Mixins.transition('box-shadow transform')};
  ${marginLevel('bottom', 'dark_cream')};
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  color: ${Colors.white};
  background-color: ${({ defaultColor }) => defaultColor};
  background-size: cover;
  box-shadow: 0 0 1px ${rgba(Colors.black, 0.04)}, 0px 2px 6px ${rgba(Colors.black, 0.04)},
    0px 10px 20px ${rgba(Colors.black, 0.04)};

  ${({ gradientColor, cover }) =>
    gradientColor &&
    cover &&
    css`
      background-image: linear-gradient(180deg, ${gradientColor} 40.89%, ${rgba(gradientColor, 0.5)} 100%),
        url(${cover});
    `};

  @media ${Points.laptop} {
    height: 400px;
  }

  @media ${Points.laptop_wide} {
    width: 100%;
    float: left;
    margin-left: ${Rhytm.column(0.42, 34)};
    margin-right: ${Rhytm.column(0.42, 34)};

    &:nth-child(1) {
      width: 640px;
      height: 600px;
      margin-bottom: 0;

      ${Category} {
        font-size: 18px;
        line-height: 22px;
      }

      ${Description} {
        display: block;
      }

      a {
        padding-left: 80px;
        padding-right: 80px;
      }
    }

    // мини
    &:nth-child(n + 2):nth-child(-n + 5) {
      width: 280px;
      height: 280px;

      ${Title} {
        font-size: 20px;
      }
    }

    //компенасация высоты для мини с 3 по 4
    &:nth-child(n + 4):nth-child(-n + 5) {
      margin-top: 1px;
    }

    // средние
    &:nth-child(n + 6) {
      width: 400px;
      height: 400px;

      ${Title} {
        font-size: 32px;
      }
    }
  }

  &:hover {
    box-shadow: ${({ isMobile }) =>
      !isMobile && `0 0 1px ${rgba(Colors.electric_blue, 0.2)}, 0 10px 20px ${rgba(Colors.electric_blue, 0.14)}`};
    transform: ${({ isMobile }) => !isMobile && 'translateY(-5px)'};
  }

  a {
    ${Mixins.resetLink()};
    width: 100%;
    height: 100%;
    display: block;
    padding: 40px;
  }
`;
